import { AuthError, ResponseJsonParsingError, SowaError, XFetchError, ResponseUnknownError } from './errors-types';
import { extractSowaErrorText } from './extractSowaErrorText';

type Data = Record<string, unknown>;

type XFetchConfig =
  | {
      timeout?: number | null;
      beacon: true;
      data: Data;
    }
  | { timeout?: number | null; beacon?: false | null; data?: Data | null };

/**
 * fetch с новыми возможностями:
 * - таймаут
 * - отправка beacon
 * - анализ ответа и выброс специальных исключений
 */
export const xfetch = async <ResponseData>(url: string, config?: XFetchConfig): Promise<ResponseData> => {
  try {
    const { beacon, data, timeout } = config || {};

    if (beacon) {
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      return await new Promise<ResponseData>((res) => {
        /**
         * sendBeacon не бросает исключений, если передавать верный тип данных.
         * success = true, если браузер успешно поставил данные data в очередь отправления (почти всегда).
         *  */
        const success = navigator.sendBeacon(url, blob);
        res({ success } as ResponseData);
      });
    }

    const timeoutSignal = typeof timeout === 'number' ? AbortSignal.timeout(timeout) : null;

    const requestInit: RequestInit = { signal: timeoutSignal };
    if (data) {
      requestInit.method = 'POST';
      requestInit.headers = { 'Content-Type': 'application/json' };
      requestInit.body = JSON.stringify(data);
    }

    const response = await fetch(url, requestInit);
    const responseText = await response.text();

    if (response.status !== 200) {
      const sowaErrorText = extractSowaErrorText(responseText);
      if (sowaErrorText) {
        throw new SowaError(response.status, sowaErrorText);
      }

      if (response.status === 401 || response.status === 403) {
        throw new AuthError(response.status, responseText);
      }

      throw new ResponseUnknownError(response.status, responseText);
    }

    try {
      return JSON.parse(responseText) as ResponseData;
    } catch (err) {
      throw new ResponseJsonParsingError(responseText);
    }
  } catch (err) {
    if (err instanceof XFetchError) {
      throw err;
    }

    throw new XFetchError(undefined, { cause: err });
  }
};
